import React from "react";
import {
	Button,
	Column,
	Heading,
	If,
	makeClassName,
	makeObjectFromArrayMap,
	Modal,
	ModalProps,
	Row,
	TruncatedText,
} from "@hex-insights/core";
import { IconContainer } from "../../../../../Components/Icon";
import { User } from "../../../../../Utilities";
import { UserFormat } from "../../../../Format";
import { UserIcon } from "../../../User";
import { useUserRecipients } from "../Recipients";
import { MessagingUserCategory, userCategories } from "./user-categories";
import modalStyles from "../Modal/styles.module.css";
import styles from "./UserCategoryRecipientsModal.module.css";

type NewMessageUserCategoryRecipientsModalProps = {
	userCategoryID: MessagingUserCategory["id"];
	userRecipientIDs: User["id"][];
	setUserRecipientIDs: React.Dispatch<React.SetStateAction<User["id"][]>>;
	onNextClick: () => void;
	onBackClick: () => void;
} & Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function NewMessageUserCategoryRecipientsModal({
	userCategoryID,
	userRecipientIDs,
	setUserRecipientIDs,
	onNextClick,
	onBackClick,
	ifRef,
	onClose,
	className,
	style,
}: NewMessageUserCategoryRecipientsModalProps) {
	const userCategory = userCategories.find((uc) => uc.id === userCategoryID)!;

	const { data: recipientsData } = useUserRecipients({
		emailIn: userCategory.members.map((m) => m.email),
	});
	const recipientEmailMap = React.useMemo(() => {
		if (!recipientsData) {
			return {};
		}
		return makeObjectFromArrayMap(recipientsData.userConnection.edges, (e) => [e.node.email, e.node]);
	}, [recipientsData]);

	const onClickUserRecipient = React.useCallback(
		(id: User["id"]) => {
			setUserRecipientIDs((prev) => {
				if (prev.includes(id)) {
					return prev.filter((e) => e !== id);
				}
				return [...prev, id];
			});
		},
		[setUserRecipientIDs],
	);

	return (
		<Modal
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(modalStyles["new-message-modal"], className)}
			style={style}
		>
			<Modal.Header>
				<Row justify="spaced-start" align="center">
					<IconContainer
						color={userCategory.iconBackgroundColor}
						className={modalStyles["user-category-button__icon-container"]}
					>
						<userCategory.Icon
							size={56}
							color={userCategory.iconColor}
							className={modalStyles["user-category-button__icon"]}
						/>
					</IconContainer>

					<Heading.H2 noMargin>{userCategory.name}</Heading.H2>
				</Row>
			</Modal.Header>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="4rem">
					<Heading.H3 noMargin style={{ textAlign: "center" }}>
						{userCategory.description}
					</Heading.H3>

					<Row
						justify="spaced-center"
						align="flex-end"
						horizontalSpacing="1rem"
						overflow="wrap"
						withVerticalSpacing
						verticalSpacing="1rem"
					>
						{userCategory.members.map((member) => {
							const user = recipientEmailMap[member.email];

							if (!user) {
								return null;
							}

							const isSelected = userRecipientIDs.includes(user.id);
							const jobTitle = user.person?.employee?.jobTitle;

							return (
								<Column
									key={user.id}
									justify="spaced-start"
									align="center"
									verticalSpacing="0.5rem"
									className={styles["user-category-recipient"]}
								>
									<If condition={!!member.description}>
										<span className={styles["user-category-recipient__member-description"]}>{member.description}</span>
									</If>

									<Button
										onClick={() => onClickUserRecipient(user.id)}
										className={makeClassName(
											styles["user-category-recipient__button"],
											isSelected ? styles["user-category-recipient__button--selected"] : "",
										)}
									>
										<Column justify="spaced-start" align="center" style={{ width: "100%" }}>
											<UserIcon user={user} size={150} />
											<Column justify="spaced-start" align="center" verticalSpacing="0.25rem" style={{ width: "100%" }}>
												<TruncatedText className={styles["user-category-recipient__name"]}>
													{UserFormat.name(user)}
												</TruncatedText>
												<If condition={!!jobTitle}>
													<TruncatedText className={styles["user-category-recipient__user-description"]}>
														{jobTitle}
													</TruncatedText>
												</If>
											</Column>
										</Column>
									</Button>
								</Column>
							);
						})}
					</Row>
				</Column>
			</Modal.Body>
			<Modal.Footer className={modalStyles["modal__footer--pass-through"]}>
				<Row justify="spaced-end" className={modalStyles["modal__footer__row"]}>
					<Button variant="secondary" onClick={onBackClick}>
						Back
					</Button>
					<Button variant="primary" onClick={onNextClick} disabled={userRecipientIDs.length === 0}>
						Next
					</Button>
				</Row>
			</Modal.Footer>
		</Modal>
	);
}
